import axios from 'axios';
import {clearAuthData, getToken} from './AuthService';
import {Venue} from "../../models/Venue";
import GeneralSettings from "../../models/GeneralSettings";
import { attachRequestId, logHttpFailure } from '../ClientLogger';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
const excludedUrls = [
    '/register',
    '/register/company',
    '/login',
    '/refresh',
    '/password/forgot',
    '/password/reset',
    '/organizationTypes',
    '/countryCodes',
    '/settings',
    '/cities',
    '/cities/search',
    '/question'
];
export const axiosInstance = axios.create({
    baseURL: API_URL,
    headers: {
        'Content-Type': 'application/json'
    }
});

const isObject = (value) => value && typeof value === 'object' && !Array.isArray(value);

const extractEntity = (payload) => {
    if (!isObject(payload)) return payload;
    return isObject(payload.data) ? payload.data : payload;
};

const normalizeUserEntity = (payload) => {
    const entity = extractEntity(payload);
    if (!isObject(entity)) return entity;
    return {
        ...entity,
        phone_number: entity.phone_number ?? entity.phoneNumber ?? '',
        phone_country_id: entity.phone_country_id ?? entity.phone_country_code ?? null,
        country_id: entity.country_id ?? entity.country_code ?? null,
    };
};

const normalizeUserCollectionPayload = (payload) => {
    if (Array.isArray(payload)) {
        return {
            users: payload.map(normalizeUserEntity),
            total: payload.length,
            currentPage: 1,
            lastPage: 1,
            meta: {},
        };
    }

    if (!isObject(payload)) {
        return { users: [], total: 0, currentPage: 1, lastPage: 1, meta: {} };
    }

    const list = Array.isArray(payload.data) ? payload.data : [];
    const meta = isObject(payload.meta) ? payload.meta : {};

    return {
        users: list.map(normalizeUserEntity),
        total: meta.total ?? payload.total ?? list.length,
        currentPage: meta.current_page ?? payload.current_page ?? 1,
        lastPage: meta.last_page ?? payload.last_page ?? 1,
        meta,
    };
};

// Request interceptor to add the auth token header to requests
axiosInstance.interceptors.request.use(
    async (config) => {
        attachRequestId(config);

        if (excludedUrls.some(url => config.url === url) || config.url?.startsWith('/integrations/registration-sessions/')) {

            return config; // Skip auth for these requests
        }
        const token = localStorage.getItem('jwt_token');

        if (token && isTokenExpired(token)) {
            if (!isRefreshing) {
                isRefreshing = true;
                try {
                    const newToken = await refreshAuthToken();
                    if (newToken) {
                        config.headers['Authorization'] = `Bearer ${newToken}`;
                    }
                    processQueue(null, newToken);
                } catch (error) {
                    processQueue(error, null);
                    throw error;
                } finally {
                    isRefreshing = false;
                }
            } else {
                await new Promise((resolve, reject) => {
                    failedQueue.push({resolve, reject});
                });
            }
        } else if (token) {
            config.headers['Authorization'] = `Bearer ${token}`;
        }
        return config;
    },
    (error) => Promise.reject(error)
);
const shouldClearAuthOn401 = (error) => {
    const url = error?.config?.url || '';
    if (url === '/login' || url === '/refresh' || url === '/me') {
        return true;
    }
    return false;
};

axiosInstance.interceptors.response.use(
    (response) => response,
    (error) => {
        logHttpFailure(error);
        if (error.response && error.response.status === 401) {
            // Only clear token for auth-critical endpoints to avoid logging out
            // on role-protected routes (e.g. org-only settings).
            if (shouldClearAuthOn401(error)) {
                localStorage.removeItem('jwt_token');
            }
            return Promise.reject(error);
        }
        return Promise.reject(error);
    }
);

export const login = async (email, password) => {
    try {
        const response = await axiosInstance.post('/login', {email, password});
        return response.data;
    } catch (error) {
        console.error('Login error:', error.response);
        throw error;
    }
};

export const loginWithGoogle = async (idToken) => {
    try {
        const response = await axiosInstance.post('/login/google', {id_token: idToken});
        return response.data;
    } catch (error) {
        console.error('Google login error:', error.response);
        throw error;
    }
};

export const postQuestion = async (name, email, question, question_part) => {
    try {
        const response = await axiosInstance.post('/question', {name, email, question, question_part});
        return response.data;
    } catch (error) {
        console.error('Question error:', error.response);
        throw error;
    }
};

export const registerUser = async (userData) => {
    try {
        const formData = new FormData();
        for (const key in userData) {
            formData.append(key, userData[key]);
        }
        const response = await axiosInstance.post('/register', formData);
        return response.data;
    } catch (error) {
        console.error('Error registering user:', error);
        throw error;
    }
};

export const registerCompany = async (payload) => {
    try {
        const response = await axiosInstance.post('/register/company', payload);
        return response.data;
    } catch (error) {
        console.error('Error registering company:', error);
        throw error;
    }
};

export const fetchIntegrationRegistrationStatus = async (sessionId) => {
    const { data } = await axiosInstance.get(`/integrations/registration-sessions/${encodeURIComponent(sessionId)}/status`);
    return data;
};

export const fetchPublicPlans = async () => {
    const { data } = await axiosInstance.get('/public/plans');
    return Array.isArray(data?.data) ? data.data : [];
};

/**
 * Plan organizacije: limiti, potrošnja i što nudi sljedeći plan.
 *
 * Potrošnja dolazi sa servera jer se dio prava broji iz stanja u bazi
 * (proizvodi, lokacije, loyalty članovi), a dio iz mjesečnih brojača
 * (discovery reach) — frontend to ne može izračunati sam.
 */
/**
 * Izvori kataloga.
 *
 * Biblioteka adaptera dolazi sa servera zajedno s `config_schema` — forma za
 * svaki adapter crta se iz te sheme, pa dodavanje novog izvora ne traži
 * promjenu sučelja.
 */
/**
 * Statistika prikaza: serija po danima/tjednima/mjesecima, ukupno i top ponude.
 *
 * Granularnost radi baza (date_trunc) — dohvaćati 180 dnevnih redaka pa ih
 * zbrajati u pregledniku isti je posao na krivom mjestu.
 */
export const fetchDiscoveryStats = async ({ days = 30, granularity = 'day' } = {}) => {
    const { data } = await axiosInstance.get('/organizations/discovery-stats', {
        params: { days, granularity },
    });
    return data;
};

export const fetchSyncAdapters = async () => {
    const { data } = await axiosInstance.get('/organizations/sync-adapters');
    return data;
};

export const fetchCatalogSources = async () => {
    const { data } = await axiosInstance.get('/organizations/catalog-sources');
    return data;
};

export const detectCatalogFeed = async (url) => {
    const { data } = await axiosInstance.post('/organizations/catalog-sources/detect', { url });
    return data;
};

export const discoverCatalogCategories = async (id) => {
    const { data } = await axiosInstance.post(`/organizations/catalog-sources/${id}/discover`);
    return data;
};

export const fetchRetailerCategoryMappings = async (params = {}) => {
    const { data } = await axiosInstance.get('/organizations/category-mappings', { params });
    return data;
};

export const searchGoogleProductCategories = async (query) => {
    const { data } = await axiosInstance.get('/organizations/category-mappings/taxonomy', { params: { q: query } });
    return data.categories || [];
};

export const updateRetailerCategoryMapping = async (id, gpt_code) => {
    const { data } = await axiosInstance.put(`/organizations/category-mappings/${id}`, { gpt_code });
    return data;
};

export const autoMapRetailerCategories = async (source_type) => {
    const { data } = await axiosInstance.post('/organizations/category-mappings/auto-map', { source_type });
    return data;
};

/** Uzorak feeda prije spremanja — ništa se ne zapisuje. */
export const testCatalogSource = async (payload) => {
    const { data } = await axiosInstance.post('/organizations/catalog-sources/test', payload);
    return data;
};

export const suggestCatalogFieldMapping = async (detected_fields, items = []) => {
    const { data } = await axiosInstance.post('/organizations/catalog-sources/suggest-mapping', {
        detected_fields,
        items: items.slice(0, 5),
    });
    return data;
};

export const createCatalogSource = async (payload) => {
    const { data } = await axiosInstance.post('/organizations/catalog-sources', payload);
    return data;
};

export const updateCatalogSource = async (id, payload) => {
    const { data } = await axiosInstance.put(`/organizations/catalog-sources/${id}`, payload);
    return data;
};

export const deleteCatalogSource = async (id) => {
    const { data } = await axiosInstance.delete(`/organizations/catalog-sources/${id}`);
    return data;
};

/** Ručno pokretanje — troši dnevnu kvotu iz plana. */
export const syncCatalogSource = async (id) => {
    const { data } = await axiosInstance.post(`/organizations/catalog-sources/${id}/sync`);
    return data;
};

export const fetchCatalogSourceRuns = async (id) => {
    const { data } = await axiosInstance.get(`/organizations/catalog-sources/${id}/runs`);
    return data;
};

export const fetchCatalogSourceRunErrors = async (id, runId) => {
    const { data } = await axiosInstance.get(`/organizations/catalog-sources/${id}/runs/${runId}/errors`);
    return data;
};

export const fetchMyPlan = async () => {
    const { data } = await axiosInstance.get('/organizations/plan');
    return data;
};

export const startOrganizationSubscription = async (plan_code) => {
    const { data } = await axiosInstance.post('/organizations/billing/subscription', { plan_code });
    return data;
};

export const fetchPartnerAttribution = async () => {
    const { data } = await axiosInstance.get('/organizations/partner-attribution');
    return data?.data ?? null;
};

export const updatePartnerAttribution = async (referral_code) => {
    const { data } = await axiosInstance.post('/organizations/partner-attribution', { referral_code });
    return data?.data ?? null;
};

export const fetchOrganizations = async ({page, perPage, searchTerm, selectedType}) => {
    try {
        const {data} = await axiosInstance.get('/organizations', {
            params: {
                page,
                perPage,
                searchTerm,
                selectedType
            }
        });
        return data;
    } catch (error) {
        console.error('Error fetching organizations:', error);
        throw error;
    }
};
// services/Http/Api.js
export const fetchManufacturers = async ({page, perPage, search}) => {
    try {
        const {data} = await axiosInstance.get('/items/manufacturers', {
            params: {
                page,
                perPage,
                search,
            }
        });
        return data;
    } catch (error) {
        console.error('Error fetching manufacturers:', error);
        throw error;
    }
};

export const fetchItems = async ({manufacturerId, perPage, page, search, sortBy, sortDirection}) => {
    try {
        const {data} = await axiosInstance.get('/items', {
            params: {
                manufacturerId,  // Filter by manufacturer ID
                perPage,         // Pagination: Number of items per page
                page,            // Pagination: Current page
                search,          // Search field: Can be model number, serial number, or product name
                sortBy,          // Sorting field
                sortDirection,   // Sorting direction (asc/desc)
            },
        });
        return data;
    } catch (error) {
        console.error('Error fetching items:', error);
        throw error;
    }
};

export const fetchCatalogProducts = async ({ perPage, page, search, sortBy, sortDirection, manufacturerId, categoryId }) => {
    try {
        const { data } = await axiosInstance.get('/organizations/catalog', {
            params: {
                perPage,
                page,
                search,
                sortBy,
                sortDirection,
                manufacturerId,
                categoryId,
            },
        });
        return data;
    } catch (error) {
        console.error('Error fetching catalog products:', error);
        throw error;
    }
};


export const fetchOrganizationTypes = async () => {
    try {
        const {data} = await axiosInstance.get('/organizationTypes');
        return data;
    } catch (error) {
        console.error('Error fetching organization types:', error);
        throw error;
    }
};

export const fetchSystemAdminDashboard = async () => {
    try {
        const { data } = await axiosInstance.get('/admin/dashboard/summary');
        return data;
    } catch (error) {
        console.error('Error fetching system admin dashboard:', error);
        throw error;
    }
};

export const fetchCountryCodes = async () => {
    try {
        const {data} = await axiosInstance.get('/countryCodes');
        return data;
    } catch (error) {
        console.error('Error fetching organization types:', error);
        throw error;
    }
};

export const fetchCities = async (params = {}) => {
    try {
        const { data } = await axiosInstance.get('/cities', { params });
        return data;
    } catch (error) {
        console.error('Error fetching cities:', error);
        throw error;
    }
};

export const searchCities = async (params = {}) => {
    try {
        const { data } = await axiosInstance.get('/cities/search', { params });
        return data;
    } catch (error) {
        console.error('Error searching cities:', error);
        throw error;
    }
};

export const createOrganization = async (postData) => {
    try {
        const {data} = await axiosInstance.post('/organizations/create', postData); // Pass postData to the post request
        return data;
    } catch (error) {
        console.error('Error creating organization:', error);
        throw error;
    }
};

export const fetchOrganizationById = async (id) => {
    try {
        if(id==null)
        {
            const response = await axiosInstance.get(`/organizations`);
            return response.data;
        }else
        {
            const response = await axiosInstance.get(`/organizations/${id}`);
            return response.data;
        }

    } catch (error) {
        console.error('Failed to fetch organization by ID:', error);
        throw error;
    }
};
export const fetchVenuesByOrganization = async (organizationId) => {
    try {
        const response = await axiosInstance.get(`/organizations/${organizationId}/venues`);
        return response.data.map(venueData => new Venue(venueData));
    } catch (error) {
        console.error('Failed to fetch venues by organization ID:', error);
        throw error;
    }
};

export const fetchUsersByOrganization = async (page = 1, perPage = 10, sortBy = 'id', sortDirection = 'asc') => {
    try {
        // Backward-compat: older call sites pass organizationId as first arg.
        if (typeof page === 'string' && page.length > 0) {
            const response = await axiosInstance.get(`/organizations/${page}/users`);
            const data = response.data;
            if (Array.isArray(data)) {
                return data.map(normalizeUserEntity);
            }
            if (Array.isArray(data?.data)) {
                return data.data.map(normalizeUserEntity);
            }
            return [];
        }

        const response = await axiosInstance.get('/customers', {
            params: {
                page,
                perPage,
                sortBy,
                sortDirection,
            },
        });
        return normalizeUserCollectionPayload(response.data);
    } catch (error) {
        console.error('Failed to fetch customers:', error);
        throw error;
    }
};

export const fetchOrganizationUsers = async (page = 1, perPage = 10, sortBy = 'id', sortDirection = 'asc') => {
    try {
        const response = await axiosInstance.get('/users', {
            params: {
                page,
                perPage,
                sortBy,
                sortDirection,
            },
        });
        return normalizeUserCollectionPayload(response.data);
    } catch (error) {
        console.error('Failed to fetch organization users:', error);
        throw error;
    }
};

export const createOrganizationStaff = async (userData) => {
    try {
        const { data } = await axiosInstance.post('/users', userData);
        return data;
    } catch (error) {
        console.error('Error creating organization staff:', error);
        throw error;
    }
};

export const updateOrganizationStaff = async (userId, userData) => {
    try {
        const { data } = await axiosInstance.put(`/users/${userId}`, userData);
        return data;
    } catch (error) {
        console.error('Error updating organization staff:', error);
        throw error;
    }
};

export const deleteOrganizationUser = async (userId) => {
    try {
        const { data } = await axiosInstance.delete(`/users/${userId}`);
        return data;
    } catch (error) {
        console.error('Error deleting organization user:', error);
        throw error;
    }
};

export const fetchUserTimeline = async () => {
    try {
        const {data} = await axiosInstance.get('/customers/timeline');
        return data;
    } catch (error) {
        console.error('Error fetching customer timeline:', error);
        throw error;
    }
};

export const updateMyProfile = async (payload) => {
    try {
        const { data } = await axiosInstance.put('/me/profile', payload);
        return data;
    } catch (error) {
        console.error('Error updating profile:', error);
        throw error;
    }
};


export const fetchVenueById = async (venueId) => {
    try {
        const response = await axiosInstance.get(`/venues/${venueId}`);
        return response.data;
    } catch (error) {
        console.error('Failed to fetch venue by ID:', error);
        throw error;
    }
};
export const fetchVenueIntegration = async (venueId) => {
    try {
        const response = await axiosInstance.get(`/venues/${venueId}/integration`);
        return response.data;  // Expected to return the API key and status
    } catch (error) {
        console.error('Error fetching venue integration:', error);
        throw error;
    }
};


export const generateVenueSecret = async (venueId) => {
    try {
        const response = await axiosInstance.put(`/venues/${venueId}/integration/generate`);
        return response.data;  // Expected to return the generated API key and secret
    } catch (error) {
        console.error('Error generating API secret for venue:', error);
        throw error;
    }
};

export const generateVenueApiKey = async (venueId) => {
    try {
        const response = await axiosInstance.put(`/venues/${venueId}/integration/generate`);
        return response.data;
    } catch (error) {
        console.error('Error generating API key for venue:', error);
        throw error;
    }
};

export const fetchVenueStatistics = async ({venueId, period, page, perPage, sortBy, sortDirection}) => {
    try {
        // Construct the API call with the venueId and the filters passed in the query params
        const response = await axiosInstance.get(`/venues/${venueId}/statistics`, {
            params: {
                period,        // Period filter (e.g., 'last_7_days', 'last_month')
                page,          // Pagination: current page
                perPage,       // Number of results per page
                sortBy,        // Sorting field (e.g., 'date', 'total_revenue')
                sortDirection, // Sorting direction ('asc' or 'desc')
            }
        });
        return response.data;
    } catch (error) {
        console.error('Error fetching venue statistics:', error);
        throw error;
    }
};


export const fetchUsersStatistics = async () => {
    try {
        const response = await axiosInstance.get('/customers/statistics');
        return response.data;
    } catch (error) {
        console.error('Failed to fetch customer statistics:', error);
        throw error;
    }
};


export const fetchAllVenues = async (params = {}) => {
    try {
        const response = await axiosInstance.get('/venues', { params });
        return response.data;
    } catch (error) {
        console.error('Failed to fetch all venues:', error);
        throw error;
    }
};

export const deleteVenue = async (venueId) => {
    try {
        const response = await axiosInstance.delete(`/venues/${venueId}`);
        return response.data;
    } catch (error) {
        console.error('Failed to delete venue:', error);
        throw error;
    }
};
export const fetchSiteSettings = async () => {
    try {
        const response = await axiosInstance.get('/settings');
        return response.data;
    } catch (error) {
        console.error('Error fetching site settings', error);
        throw error;
    }
};
export const addVenueToOrganization = async (venueData) => {
    try {
        const response = await axiosInstance.post(`/venues`, venueData);
        return response.data;
    } catch (error) {
        console.error('Failed to add venue to organization:', error);
        throw error;
    }
};

export const updateVenue = async (venueId, venueData) => {
    try {
        const response = await axiosInstance.put(`/venues/${venueId}`, venueData);
        return response.data;
    } catch (error) {
        console.error('Failed to update venue:', error);
        throw error;
    }
};
export const addUserToOrganization = async (organizationId, userData) => {
    try {
        const response = await axiosInstance.post(`/organizations/${organizationId}/users`, userData);
        return response.data;
    } catch (error) {
        console.error('Failed to add user to organization:', error);
        throw error;
    }
};
export const addCustomerToOrganization = async (userData) => {
    try {
        const response = await axiosInstance.post('/customers', userData);
        return response.data;
    } catch (error) {
        console.error('Failed to add customer:', error);
        throw error;
    }
};

export const updateCustomer = async (customerId, userData) => {
    try {
        const response = await axiosInstance.put(`/customers/${customerId}`, userData);
        return response.data;
    } catch (error) {
        console.error('Failed to update customer:', error);
        throw error;
    }
};

export const deleteCustomer = async (customerId) => {
    try {
        const response = await axiosInstance.delete(`/customers/${customerId}`);
        return response.data;
    } catch (error) {
        console.error('Failed to delete customer:', error);
        throw error;
    }
};

export const fetchCustomerById = async (customerId) => {
    try {
        const response = await axiosInstance.get(`/customers/${customerId}`);
        return response.data;
    } catch (error) {
        console.error('Failed to fetch customer:', error);
        throw error;
    }
};

export const fetchCustomerTransactions = async (
    userId,
    {
        page = 1,
        perPage = 50,
        fromDate = '2000-01-01',
        toDate = new Date().toISOString().slice(0, 10),
    } = {}
) => {
    try {
        const response = await axiosInstance.get(`/transactions/users/${userId}/list`, {
            params: {
                page,
                perPage,
                from_date: fromDate,
                to_date: toDate,
            },
        });
        return response.data;
    } catch (error) {
        console.error('Failed to fetch customer transactions:', error);
        throw error;
    }
};

export const addUserToVenue = async (venueId, userData) => {
    try {
        const response = await axiosInstance.post(`/venues/${venueId}/users`, userData);
        return response.data;
    } catch (error) {
        console.error('Failed to add user to venue:', error);
        throw error;
    }
};
export const fetchAllUsers = async () => {
    // TODO: Implement fetchAllUsers
    return [];
};

export const fetchUsersByVenue = async (id) => {
    // TODO: Implement fetchUsersByVenue
    return [];
};

export const addUser = async (id, user) => {
    // TODO: Implement addUser
    return {};
};

export const fetchRolesForOrganization = async () => {
    try {
        const response = await axiosInstance.get(`/organizations/roles`);
        return response.data;
    } catch (error) {
        console.error('Failed to fetch roles for organization:', error);
        throw error;
    }
};
export const fetchDashboardData = async () => {
    try {
        const response = await axiosInstance.get('/dashboard');
        return response.data;
    } catch (error) {
        console.error('Failed to fetch dashboard data:', error);
        throw error;
    }
};

export const fetchRolesForVenue = async () => {
    try {
        const response = await axiosInstance.get(`/venues/roles`);
        return response.data;
    } catch (error) {
        console.error('Failed to fetch roles for venue:', error);
        throw error;
    }
};

export const fetchRolesForSystem = async () => {
    try {
        const response = await axiosInstance.get('/system/roles');
        return response.data;
    } catch (error) {
        console.error('Failed to fetch roles for system:', error);
        throw error;
    }
};

export const fetchTransactionStats = async ({
                                                period,
                                                page = 1,
                                                perPage = 10,
                                                sortBy = 'date',
                                                sortDirection = 'asc'
                                            }) => {
    try {
        const response = await axiosInstance.get('/transactions/stats', {
            params: {
                period,
                page,
                perPage,
                sortBy,
                sortDirection
            }
        });
        return response.data;
    } catch (error) {
        console.error('Failed to fetch transaction stats', error);
        throw error;
    }
};
export const fetchDemographicsData = async (params) => {
    try {
        const response = await axiosInstance.get('/transactions/demographics', {params});
        return response.data;
    } catch (error) {
        console.error('Failed to fetch demographics data:', error);
        throw error;
    }
};
export const fetchTransactionProgress = async (params) => {
    try {
        const response = await axiosInstance.get('/transactions/progress', {params});
        return response.data;
    } catch (error) {
        console.error('Failed to fetch demographics data:', error);
        throw error;
    }
}

export const fetchCrossSellEarningStats = async ({
    period,
    page = 1,
    perPage = 25,
    sortBy = 'period',
    sortDirection = 'asc',
    grouping = 'daily'
}) => {
    try {
        const { data } = await axiosInstance.get('/cross-sell-earnings/stats', {
            params: {
                period,
                page,
                perPage,
                sortBy,
                sortDirection,
                grouping
            }
        });
        return data;
    } catch (error) {
        console.error('Failed to fetch cross-sell earnings stats', error);
        throw error;
    }
};

export const fetchItemsWithFilters = async ({
                                                modelNumber,
                                                serialNumber,
                                                manufacturerId,
                                                status,
                                                startDate,
                                                endDate,
                                                page = 1,
                                                perPage = 10
                                            }) => {
    try {
        const {data} = await axiosInstance.get('/items', {
            params: {
                modelNumber,
                serialNumber,
                manufacturerId,
                status,
                startDate,
                endDate,
                page,
                perPage
            }
        });
        return data;
    } catch (error) {
        console.error('Error fetching items with filters:', error);
        throw error;
    }
};

export const fetchBestSellingItems = async ({period, page = 1, perPage = 10}) => {
    try {
        const {data} = await axiosInstance.get('/items/best-selling', {
            params: {
                period,
                page,
                perPage
            }
        });
        return data;
    } catch (error) {
        console.error('Error fetching best selling items:', error);
        throw error;
    }
};

export const fetchWarrantyExpiryAlerts = async ({period = '30_days', page = 1, perPage = 10}) => {
    try {
        const {data} = await axiosInstance.get('/items/warranty-expiry', {
            params: {
                period,
                page,
                perPage
            }
        });
        return data;
    } catch (error) {
        console.error('Error fetching warranty expiry alerts:', error);
        throw error;
    }
};

export const fetchItemDetails = async (itemId) => {
    try {
        const {data} = await axiosInstance.get(`/items/${itemId}`);
        return data;
    } catch (error) {
        console.error('Error fetching item details:', error);
        throw error;
    }
};

export const fetchProductDetails = async (productId) => {
    try {
        const {data} = await axiosInstance.get(`/items/manufacturerItem/${productId}`);
        return data;
    } catch (error) {
        console.error('Error fetching product details:', error);
        throw error;
    }
};

export const fetchItemTransactions = async (itemId) => {
    try {
        const {data} = await axiosInstance.get(`/items/${itemId}/transactions`);
        return data;
    } catch (error) {
        console.error('Error fetching item transactions:', error);
        throw error;
    }
};

export const updateItemDetails = async (itemId, updatedData) => {
    try {
        const {data} = await axiosInstance.put(`/items/${itemId}`, updatedData);
        return data;
    } catch (error) {
        console.error('Error updating item details:', error);
        throw error;
    }
};

export const fetchSoldItems = async ({itemId, page = 1, perPage = 10, sortBy = 'date', sortDirection = 'asc'}) => {
    try {
        const {data} = await axiosInstance.get(`/items/manufacturerItem/${itemId}/soldItems`, {
            params: {
                page,
                perPage,
                sortBy,
                sortDirection
            }
        });
        return data;
    } catch (error) {
        console.error('Error fetching sold items:', error);
        throw error;
    }
};

export const fetchSalesInsights = async ({period, page = 1, perPage = 10}) => {
    try {
        const {data} = await axiosInstance.get('/items/sales-insights', {
            params: {
                period,
                page,
                perPage
            }
        });
        return data;
    } catch (error) {
        console.error('Error fetching sales insights:', error);
        throw error;
    }
};

export const fetchItemPerformanceCharts = async (itemId) => {
    try {
        const {data} = await axiosInstance.get(`/items/manufacturerItem/${itemId}/performance-charts`);
        return data;
    } catch (error) {
        console.error('Error fetching chart data:', error);
        throw error;
    }
};

export const fetchPerformanceInsights = async (params = {}) => {
    try {
        const {data} = await axiosInstance.get('/items/performance-insights', { params });
        return data;
    } catch (error) {
        console.error('Error fetching performance insights:', error);
        throw error;
    }
};

export const fetchWarrantyAlerts = async (params = {}) => {
    try {
        const {data} = await axiosInstance.get('/items/warranty-alerts', { params });
        return data;
    } catch (error) {
        console.error('Error fetching warranty alerts:', error);
        throw error;
    }
};

export const fetchItemPerformanceInsights = async (itemId, page, perPage, period, sortField, sortDirection) => {
    try {
        const {data} = await axiosInstance.get(`/items/manufacturerItem/${itemId}/performance-insights`, {
            params: {
                page,
                perPage,
                period,
                sortBy: sortField,
                sortDirection,
            }
        });
        return data;
    } catch (error) {
        console.error('Error fetching performance insights:', error);
        throw error;
    }
};

export const fetchCampaigns = async ({page = 1, perPage = 10, sortBy = 'name', sortDirection = 'asc'}) => {
    try {
        const response = await axiosInstance.get('/campaigns', {
            params: {
                page,
                perPage,
                sortBy,
                sortDirection,
            },
        });
        return response;
    } catch (error) {
        console.error('Error fetching campaigns:', error);
        throw error;
    }
};

export const createCampaign = async (campaignData) => {
    try {
        const response = await axiosInstance.post('/campaigns', campaignData);
        return response.data;
    } catch (error) {
        console.error('Error creating campaign:', error);
        throw error;
    }
};
export const calculateCampaign = async (campaignData) => {
    try {
        const response = await axiosInstance.post('/campaigns/calculate', campaignData);
        return response.data;
    } catch (error) {
        console.error('Error creating campaign:', error);
        throw error;
    }
};

export const fetchCampaign = async (campaignId) => {
    try {
        const response = await axiosInstance.get(`/campaigns/${campaignId}`);
        return response.data;
    } catch (error) {
        console.error('Error fetching campaign:', error);
        throw error;
    }
};
export const GetMe = async () => {
    try {
        const response = await axiosInstance.get('/me');
        const userData = normalizeUserEntity(response.data);
        saveUserData(userData); // Save user data to local storage
        return userData;
    } catch (error) {
        console.error('Error fetching user data from /me', error);
        throw error;
    }
};
const saveToken = (token) => {
    try {
        localStorage.setItem('jwt_token', token);
    } catch (error) {
        console.error('Failed to save token to storage', error);
    }
};
const saveUserData = (userData) => {
    try {
        localStorage.setItem('userData', JSON.stringify(userData));
    } catch (error) {
        console.error('Failed to save user data to storage', error);
    }
};
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
    failedQueue.forEach(prom => {
        if (error) {
            prom.reject(error);
        } else {
            prom.resolve(token);
        }
    });

    failedQueue = [];
};
export const refreshAuthToken = async () => {
    try {
        const token = getToken();

        if (!token) return null;

        // Prevent multiple refreshes by queuing requests
        if (isRefreshing) {
            return new Promise((resolve, reject) => {
                failedQueue.push({resolve, reject});
            });
        }

        isRefreshing = true;

        const response = await axios.post(API_URL + '/refresh', {token}, {
            headers: {
                'Content-Type': 'application/json',
            }
        });

        const newToken = response.data.token;
        saveToken(newToken);
        processQueue(null, newToken);
        return newToken;

    } catch (error) {
        processQueue(error, null);
        console.error('Error refreshing token', error);
        return null;
    } finally {
        isRefreshing = false;
    }
};
export const updateCampaign = async (id, campaignData) => {
    try {
        const response = await axiosInstance.put(`/campaigns/${id}`, campaignData);
        return response.data;
    } catch (error) {
        console.error('Error updating campaign:', error);
        throw error;
    }
};

export const deleteCampaign = async (id) => {
    try {
        const response = await axiosInstance.delete(`/campaigns/${id}`);
        return response.data;
    } catch (error) {
        console.error('Error deleting campaign:', error);
        throw error;
    }
};

// Fetch available currencies from site settings
export const fetchCurrencies = async () => {
    try {
        const response = await axiosInstance.get('/settings');
        return response.data?.currencies || [];
    } catch (error) {
        console.error('Failed to fetch currencies:', error);
        // Fallback default currencies
        return [
            { id: 1, code: 'EUR', name: 'Euro', symbol: '€' },
            { id: 2, code: 'USD', name: 'US Dollar', symbol: '$' },
            { id: 3, code: 'GBP', name: 'British Pound', symbol: '£' }
        ];
    }
};

export const fetchTimezones = async () => {
    // TODO: Implement GET /timezones when backend is ready
    return [
        {id: 1, name: 'UTC'},
        {id: 2, name: 'America/New_York'},
        {id: 3, name: 'Europe/London'}
    ];
};

export const fetchLanguages = async () => {
    // TODO: Implement GET /languages when backend is ready
    return [
        {id: 1, code: 'en', name: 'English'},
        {id: 2, code: 'es', name: 'Spanish'},
        {id: 3, code: 'fr', name: 'French'}
    ];
};

export const saveGeneralSettings = async (data) => {
    // TODO: Implement PUT /organizations/:id/settings when backend is ready
    console.log('Saving general settings:', data);
    return {message: 'Settings saved (stub)'};
};

export const fetchBills = async () => {
    // TODO: Implement GET /organization_bills when backend is ready
    return [
        {
            id: '1',
            billing_period_start: '2025-06-01',
            billing_period_end: '2025-06-30',
            total_amount: 100.00,
            transaction_fee_amount: 80.00,
            cross_sell_earnings: 20.00,
            status: 'pending'
        }
    ];
};

export const fetchInvoices = async () => {
    // TODO: Implement GET /organization_invoices when backend is ready
    return [
        {
            id: '1',
            bill_id: '1',
            invoice_number: 'INV-2025-06-001',
            issued_at: '2025-06-01T00:00:00Z',
            total_amount: 100.00,
            pdf_url: 'https://example.com/invoices/1.pdf'
        }
    ];
};
export const fetchGeneralSettings = async () => {
    try {
        const response = await axiosInstance.get('/organizations/settings/general');
        return GeneralSettings.fromApiResponse(response.data || {});
    } catch (error) {
        console.error('Error fetching general settings:', error);
        throw error;
    }
};

export const fetchDeliverySettings = async () => {
    try {
        const { data } = await axiosInstance.get('/organizations/settings/delivery');
        return data;
    } catch (error) {
        console.error('Error fetching delivery settings:', error);
        throw error;
    }
};

export const updateDeliverySettings = async ({ scope, countryIds = [] }) => {
    try {
        const { data } = await axiosInstance.put('/organizations/settings/delivery', {
            scope,
            country_ids: countryIds,
        });
        return data;
    } catch (error) {
        console.error('Error updating delivery settings:', error);
        throw error;
    }
};

export const createLogoUpload = async ({ filename, contentType }) => {
    try {
        const { data } = await axiosInstance.post('/organizations/settings/general/logo-upload', {
            filename,
            content_type: contentType
        });
        return data;
    } catch (error) {
        console.error('Error creating logo upload:', error);
        throw error;
    }
};
export const updateBasicInfo = async ({
    name,
    description,
    organization_type_id,
    founded_date,
    address,
    country_id,
    phone,
    phone_country_id,
    website,
    email,
    tax_id,
    vat_id,
    logo_key
}) => {
    try {
        const payload = {
            name,
            description,
            organization_type_id,
            founded_date,
            address,
            country_id,
            phone,
            phone_country_id,
            website,
            email,
            tax_id,
            vat_id,
            logo_key
        };
        console.log('API Basic Info payload:', payload);
        const {data} = await axiosInstance.put(
            '/organizations/settings/general/basic-info',
            payload,
            {headers: {'Content-Type': 'application/json'}}
        );
        return GeneralSettings.fromApiResponse(data);
    } catch (error) {
        console.error('Error updating basic info:', error);
        throw error;
    }
};

export const updateLocalization = async ({ timezone_id, language_id }) => {
    try {
        const payload = { timezone_id, language_id };
        console.log('API Localization payload:', payload);
        const {data} = await axiosInstance.put(
            '/organizations/settings/general/localization',
            payload
        );
        return GeneralSettings.fromApiResponse(data);
    } catch (error) {
        console.error('Error updating localization:', error);
        throw error;
    }
};

// UPDATE INTEGRATION ENDPOINTS
export const updateIntegration = async ({
                                            default_item_endpoint,
                                            default_item_api_endpoint,
                                            default_user_parameter
                                        }) => {
    try {
        const payload = {default_item_endpoint, default_item_api_endpoint, default_user_parameter};
        console.log('API Integration payload:', payload);
        const {data} = await axiosInstance.put(
            '/organizations/settings/general/integration',
            payload
        );
        return GeneralSettings.fromApiResponse(data);
    } catch (error) {
        console.error('Error updating integration settings:', error);
        throw error;
    }
};

// UPDATE NOTIFICATIONS
export const updateNotifications = async ({
                                              notification_preferences,
                                              send_notifications_via_email,
                                              send_notifications_via_sms,
                                              send_notifications_via_push,
                                              notification_email
                                          }) => {
    try {
        const payload = {
            notification_preferences,
            send_notifications_via_email,
            send_notifications_via_sms,
            send_notifications_via_push,
            notification_email
        };
        console.log('API Notifications payload:', payload);
        const {data} = await axiosInstance.put(
            '/organizations/settings/general/notifications',
            payload
        );
        return GeneralSettings.fromApiResponse(data);
    } catch (error) {
        console.error('Error updating notification settings:', error);
        throw error;
    }
};

export const fetchBillingSettings = async () => {
    try {
        const { data } = await axiosInstance.get('/organizations/settings/billing');
        return data;
    } catch (error) {
        console.error('Error fetching billing settings:', error);
        throw error;
    }
};

export const updateBillingSettings = async ({ billing_email, auto_payment_enabled }) => {
    try {
        const payload = { billing_email, auto_payment_enabled };
        const { data } = await axiosInstance.put('/organizations/settings/billing', payload);
        return data;
    } catch (error) {
        console.error('Error updating billing settings:', error);
        throw error;
    }
};

export const updatePaymentMethod = async (paymentMethodId) => {
    // TODO: Implement POST /organization_billing/payment_method when backend is ready
    console.log('Updating payment method:', paymentMethodId);
    return {
        payment_method_id: paymentMethodId,
        card_brand: 'visa',
        card_last_four: '4242',
        auto_payment_enabled: false
    };
};

export const toggleAutoPayment = async (autoPaymentEnabled) => {
    return updateBillingSettings({ auto_payment_enabled: autoPaymentEnabled });
};

export const fetchBillingProfile = async () => {
    try {
        const { data } = await axiosInstance.get('/organizations/billing');
        return data;
    } catch (error) {
        console.error('Error fetching billing profile:', error);
        throw error;
    }
};

export const createBillingSetupIntent = async () => {
    try {
        const { data } = await axiosInstance.post('/organizations/billing/setup-intent');
        return data;
    } catch (error) {
        console.error('Error creating billing setup intent:', error);
        throw error;
    }
};

export const attachBillingPaymentMethod = async (payment_method_id) => {
    try {
        const { data } = await axiosInstance.post('/organizations/billing/payment-method', {
            payment_method_id,
        });
        return data;
    } catch (error) {
        console.error('Error attaching billing payment method:', error);
        throw error;
    }
};

export const fetchContracts = async () => {
    try {
        const { data } = await axiosInstance.get('/organizations/settings/contracts');
        return data;
    } catch (error) {
        console.error('Error fetching contracts:', error);
        throw error;
    }
};

export const getActiveContract = async () => {
    try {
        const { data } = await axiosInstance.get('/organizations/settings/contracts/active');
        return data;
    } catch (error) {
        console.error('Error fetching active contract:', error);
        throw error;
    }
};

export const saveContract = async (payload) => {
    try {
        const { data } = await axiosInstance.put(`/organizations/settings/contracts/${payload.id}`, payload);
        return data;
    } catch (error) {
        console.error('Error saving contract:', error);
        throw error;
    }
};

// Updated Api.js functions

export const fetchIntegrations = async () => {
    try {
        const { data } = await axiosInstance.get('/organizations/integrators');
        return data;
    } catch (error) {
        console.error('Error fetching integrations:', error);
        throw error;
    }
};

export const saveIntegration = async (data) => {
    try {
        if (data.id) {
            // Update existing
            const { data: response } = await axiosInstance.put(`/organizations/integrators/${data.id}`, data);
            return response;
        } else {
            // Create new
            const { data: response } = await axiosInstance.post('/organizations/integrators', data);
            return response;
        }
    } catch (error) {
        console.error('Error saving integration:', error);
        throw error;
    }
};

export const deleteIntegration = async (id) => {
    try {
        const { data } = await axiosInstance.delete(`/organizations/integrators/${id}`);
        return data;
    } catch (error) {
        console.error('Error deleting integration:', error);
        throw error;
    }
};

export const fetchCsvUploads = async () => {
    try {
        const { data } = await axiosInstance.get('/organizations/csv-uploads');
        return data;
    } catch (error) {
        console.error('Error fetching CSV uploads:', error);
        throw error;
    }
};

export const uploadCsvImport = async (file, importType = 'items') => {
    try {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('import_type', importType);

        const { data } = await axiosInstance.post('/organizations/imports/csv', formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
        return data;
    } catch (error) {
        console.error('Error uploading CSV import:', error);
        throw error;
    }
};

export const analyzeCsvImport = async (importId) => {
    try {
        const { data } = await axiosInstance.post(`/organizations/imports/csv/${importId}/analyze`);
        return data;
    } catch (error) {
        console.error('Error analyzing CSV import:', error);
        throw error;
    }
};

export const suggestCsvImportFieldMapping = async (importId) => {
    const { data } = await axiosInstance.post(`/organizations/imports/csv/${importId}/suggest-fields`);
    return data;
};

export const suggestCsvImportCategoryMappings = async (importId) => {
    const { data } = await axiosInstance.post(`/organizations/imports/csv/${importId}/suggest-categories`);
    return data;
};

export const saveCsvImportFieldMapping = async (importId, field_mapping) => {
    const { data } = await axiosInstance.put(`/organizations/imports/csv/${importId}/field-map`, { field_mapping });
    return data;
};

export const fetchCsvImports = async () => {
    try {
        const { data } = await axiosInstance.get('/organizations/imports/csv');
        return data;
    } catch (error) {
        console.error('Error fetching CSV imports:', error);
        throw error;
    }
};

export const fetchCsvImport = async (importId) => {
    try {
        const { data } = await axiosInstance.get(`/organizations/imports/csv/${importId}`);
        return data;
    } catch (error) {
        console.error('Error fetching CSV import:', error);
        throw error;
    }
};

export const updateSecuritySettings = async (data) => {
    const { data: response } = await axiosInstance.put('/me/password', {
        current_password: data.current_password,
        new_password: data.new_password,
        new_password_confirmation: data.confirm_password,
    });
    return response;
};

export const regenerateApiKey = async (organizationId) => {
    // TODO: Implement POST /organizations/:id/api_key when backend is ready
    console.log('Regenerating API key for organization:', organizationId);
    return 'new_api_key_123';
};

export const toggleApiStatus = async (organizationId, isActive) => {
    // TODO: Implement PUT /organizations/:id/api_status when backend is ready
    console.log('Toggling API status for organization:', organizationId, isActive);
    return {message: 'API status updated (stub)'};
};

export const fetchPointsSettings = async () => {
    try {
        const { data } = await axiosInstance.get('/organizations/settings/points');
        return data;
    } catch (error) {
        console.error('Error fetching points settings:', error);
        throw error;
    }
};
export const fetchOrgManufacturers = async ({ withServiceProviders = false, all = false } = {}) => {
    try {
        const { data } = await axiosInstance.get('/organizations/manufacturers', {
            params: {
                with_service_providers: withServiceProviders,
                all: all || undefined,
            },
        });
        return data;
    } catch (error) {
        console.error('Error fetching manufacturers:', error);
        throw error;
    }
};

export const fetchOrganizationMenu = async () => {
    try {
        const { data } = await axiosInstance.get('/organizations/menu');
        return data;
    } catch (error) {
        console.error('Error fetching organization menu:', error);
        throw error;
    }
};

export const fetchCategoriesByManufacturer = async (manufacturerId) => {
    try {
        const { data } = await axiosInstance.get(`/organizations/categories/manufacturer/${manufacturerId}`);
        return data;
    } catch (error) {
        console.error('Error fetching categories by manufacturer:', error);
        throw error;
    }
};

export const fetchCatalogCategories = async () => {
    try {
        const { data } = await axiosInstance.get('/organizations/catalog/categories');
        return data;
    } catch (error) {
        console.error('Error fetching catalog categories:', error);
        throw error;
    }
};

export const fetchCatalogManufacturers = async () => {
    try {
        const { data } = await axiosInstance.get('/organizations/catalog/manufacturers');
        return data;
    } catch (error) {
        console.error('Error fetching catalog manufacturers:', error);
        throw error;
    }
};

export const fetchOrgCategories = async (search = '') => {
    try {
        const { data } = await axiosInstance.get('/organizations/categories', {
            params: search ? { search } : {},
        });
        return data;
    } catch (error) {
        console.error('Error fetching categories:', error);
        throw error;
    }
};

export const saveCsvImportMappings = async (importId, payload) => {
    try {
        const { data } = await axiosInstance.post(`/organizations/imports/csv/${importId}/map`, payload);
        return data;
    } catch (error) {
        console.error('Error saving CSV mappings:', error);
        throw error;
    }
};

export const runCsvImport = async (importId) => {
    try {
        const { data } = await axiosInstance.post(`/organizations/imports/csv/${importId}/run`);
        return data;
    } catch (error) {
        console.error('Error running CSV import:', error);
        throw error;
    }
};

export const fetchCsvImportProgress = async (importId) => {
    try {
        const { data } = await axiosInstance.get(`/organizations/imports/csv/${importId}/progress`);
        return data;
    } catch (error) {
        console.error('Error fetching CSV import progress:', error);
        throw error;
    }
};

export const fetchOrgVenuesForImport = async (params = {}) => {
    try {
        const { data } = await axiosInstance.get('/venues', { params: { perPage: 200, ...params } });
        return data;
    } catch (error) {
        console.error('Error fetching org venues for import:', error);
        throw error;
    }
};
export const fetchServiceProviderCategoriesByManufacturer = async (manufacturerId) => {
    try {
        const { data } = await axiosInstance.get(`/service-provider-categories/manufacturer/${manufacturerId}`);
        return data;
    } catch (error) {
        console.error('Error fetching service provider categories by manufacturer:', error);
        throw error;
    }
};

export const fetchServiceProviders = async ({ manufacturer_id, category_id }) => {
    try {
        const { data } = await axiosInstance.get('/service-providers', {
            params: { manufacturer_id, category_id }
        });
        return data;
    } catch (error) {
        console.error('Error fetching service providers:', error);
        throw error;
    }
};

export const fetchServiceProviderMappings = async () => {
    try {
        const { data } = await axiosInstance.get('/service-provider-mappings');
        return data;
    } catch (error) {
        console.error('Error fetching service provider mappings:', error);
        throw error;
    }
};

export const createServiceProviderMapping = async (payload) => {
    try {
        const { data } = await axiosInstance.post('/service-provider-mappings', payload);
        return data;
    } catch (error) {
        console.error('Error creating service provider mapping:', error);
        throw error;
    }
};

export const deleteServiceProviderMapping = async (id) => {
    try {
        await axiosInstance.delete(`/service-provider-mappings/${id}`);
    } catch (error) {
        console.error('Error deleting service provider mapping:', error);
        throw error;
    }
};
export const savePointsSettings = async (payload) => {
    try {
        const { data } = await axiosInstance.put(`/organizations/settings/points/${payload.id}`, payload);
        return data;
    } catch (error) {
        console.error('Error saving points settings:', error);
        throw error;
    }
};
export const updateUserInfo = async (data) => {
    // TODO: Implement PUT /organization_points_settings when backend is ready
    console.log('Saving points settings:', data);
    return {message: 'Points settings saved (stub)'};
};

// Warranty Template Endpoints
export const fetchWarrantyTemplates = async ({ page = 1, perPage = 10 }) => {
    try {
        const { data } = await axiosInstance.get('/organizations/warranty-templates', {
            params: { page, perPage }
        });
        return data;
    } catch (error) {
        console.error('Error fetching warranty templates:', error);
        throw error;
    }
};

export const fetchWarrantyTemplate = async (id) => {
    try {
        const { data } = await axiosInstance.get(`/organizations/warranty-templates/${id}`);
        return data;
    } catch (error) {
        console.error('Error fetching warranty template:', error);
        throw error;
    }
};

export const createWarrantyTemplate = async (templateData) => {
    try {
        const { data } = await axiosInstance.post('/organizations/warranty-templates', templateData);
        return data;
    } catch (error) {
        console.error('Error creating warranty template:', error);
        throw error;
    }
};

export const updateWarrantyTemplate = async (id, templateData) => {
    try {
        const { data } = await axiosInstance.put(`/organizations/warranty-templates/${id}`, templateData);
        return data;
    } catch (error) {
        console.error('Error updating warranty template:', error);
        throw error;
    }
};

export const deleteWarrantyTemplate = async (id) => {
    try {
        const { data } = await axiosInstance.delete(`/organizations/warranty-templates/${id}`);
        return data;
    } catch (error) {
        console.error('Error deleting warranty template:', error);
        throw error;
    }
};
export const fetchActiveWarranties = async ({
    page = 1,
    perPage = 10,
    searchTerm = '',
    sortBy = '',
    sortDirection = 'asc',
} = {}) => {
    try {
        const { data } = await axiosInstance.get('/organizations/active-warranties', {
            params: { page, perPage, searchTerm, sortBy, sortDirection }
        });
        return data;
    } catch (error) {
        console.error('Error fetching active warranties:', error);
        throw error;
    }
};

export const fetchWarrantyTemplateTypes = async () => {
    try {
        const { data } = await axiosInstance.get('/organizations/warranty-template-types');
        return data;
    } catch (error) {
        console.error('Error fetching warranty template types:', error);
        throw error;
    }
};

export const fetchCategories = async ({ searchTerm = '', limit = 50 } = {}) => {
    try {
        const { data } = await axiosInstance.get('/organizations/warranty-categories', {
            params: { searchTerm, limit }
        });
        return data;
    } catch (error) {
        console.error('Error fetching categories:', error);
        throw error;
    }
};

export const fetchManufacturerItems = async ({ searchTerm = '', limit = 50 } = {}) => {
    try {
        const { data } = await axiosInstance.get('/organizations/manufacturer-items', {
            params: { searchTerm, limit }
        });
        return data;
    } catch (error) {
        console.error('Error fetching manufacturer items:', error);
        throw error;
    }
};

export const fetchRetailerBestOffers = async (params = {}) => {
    const { data } = await axiosInstance.get('/retailer/discounts/best-offers', { params });
    return data;
};

export const fetchRetailerDiscountFilterOptions = async () => {
    const { data } = await axiosInstance.get('/retailer/discounts/filter-options');
    return data;
};

export const fetchRetailerDiscounts = async (params = {}) => {
    const { data } = await axiosInstance.get('/retailer/discounts', { params });
    return data;
};

// Retailer Distributors
export const fetchRetailerDistributors = async (params = {}) => {
    const { data } = await axiosInstance.get('/retailer/distributors', { params });
    return data;
};

export const fetchRetailerDistributorStats = async () => {
    const { data } = await axiosInstance.get('/retailer/distributors/stats');
    return data;
};

export const fetchRetailerDistributorDetail = async (distributorId) => {
    const { data } = await axiosInstance.get(`/retailer/distributors/${distributorId}`);
    return data;
};

export const fetchRetailerDistributorDiscounts = async (distributorId, params = {}) => {
    const { data } = await axiosInstance.get(`/retailer/distributors/${distributorId}/discounts`, { params });
    return data;
};

export const fetchAvailableDistributors = async (params = {}) => {
    const { data } = await axiosInstance.get('/retailer/distributors/available', { params });
    return data;
};

export const fetchDistributorBestOffers = async (params = {}) => {
    const { data } = await axiosInstance.get('/distributor/discounts/best-offers', { params });
    return data;
};

export const fetchDistributorDiscounts = async (params = {}) => {
    const { data } = await axiosInstance.get('/distributor/discounts', { params });
    return data;
};

export const fetchManufacturerDiscounts = async (params = {}) => {
    const { data } = await axiosInstance.get('/manufacturer/discounts', { params });
    return data;
};

// Scanned bills (organization)
export const fetchOrgScannedBills = async (params = {}) => {
    const { data } = await axiosInstance.get('/scanned-bills', { params });
    return data;
};

export const fetchOrgScannedBill = async (id) => {
    const { data } = await axiosInstance.get(`/scanned-bills/${id}`);
    return data;
};

export const reviewOrgScannedBill = async (id, payload) => {
    const { data } = await axiosInstance.put(`/scanned-bills/${id}/review`, payload);
    return data;
};

export const fetchOrgScannedBillStats = async () => {
    const { data } = await axiosInstance.get('/scanned-bills/statistics');
    return data;
};

// ============================================
// RETAILER BILL APPROVALS
// ============================================

export const fetchRetailerPendingBills = async (params = {}) => {
    const { data } = await axiosInstance.get('/bill-approvals', { params });
    return data;
};

export const fetchRetailerPendingBillsStats = async () => {
    const { data } = await axiosInstance.get('/bill-approvals/statistics');
    return data;
};

export const fetchRetailerPendingBill = async (id) => {
    const { data } = await axiosInstance.get(`/bill-approvals/${id}`);
    return data;
};

export const approveRetailerBill = async (id, payload = {}) => {
    const { data } = await axiosInstance.post(`/bill-approvals/${id}/approve`, payload);
    return data;
};

export const rejectRetailerBill = async (id, payload = {}) => {
    const { data } = await axiosInstance.post(`/bill-approvals/${id}/reject`, payload);
    return data;
};

// ============================================
// MANUFACTURER PRODUCT APPROVALS
// ============================================

export const fetchManufacturerPendingProducts = async (params = {}) => {
    const { data } = await axiosInstance.get('/manufacturer/product-approvals', { params });
    return data;
};

export const fetchManufacturerPendingProductsStats = async () => {
    const { data } = await axiosInstance.get('/manufacturer/product-approvals/statistics');
    return data;
};

export const fetchManufacturerPendingProduct = async (id) => {
    const { data } = await axiosInstance.get(`/manufacturer/product-approvals/${id}`);
    return data;
};

export const approveManufacturerProduct = async (id, payload = {}) => {
    const { data } = await axiosInstance.post(`/manufacturer/product-approvals/${id}/approve`, payload);
    return data;
};

export const rejectManufacturerProduct = async (id, payload = {}) => {
    const { data } = await axiosInstance.post(`/manufacturer/product-approvals/${id}/reject`, payload);
    return data;
};

export const mergeManufacturerProduct = async (id, payload = {}) => {
    const { data } = await axiosInstance.post(`/manufacturer/product-approvals/${id}/merge`, payload);
    return data;
};

export const searchManufacturerProducts = async (params = {}) => {
    const { data } = await axiosInstance.get('/manufacturer/products/search', { params });
    return data;
};

// Organization Roles API functions
export const fetchOrganizationRoles = async () => {
    try {
        const { data } = await axiosInstance.get('/organization-roles');
        return data;
    } catch (error) {
        console.error('Error fetching organization roles:', error);
        throw error;
    }
};

export const fetchOrganizationRole = async (id) => {
    try {
        const { data } = await axiosInstance.get(`/organization-roles/${id}`);
        return data.data;
    } catch (error) {
        console.error('Error fetching organization role:', error);
        throw error;
    }
};

export const createOrganizationRole = async (roleData) => {
    try {
        const { data } = await axiosInstance.post('/organization-roles', roleData);
        return data;
    } catch (error) {
        console.error('Error creating organization role:', error);
        throw error;
    }
};

export const updateOrganizationRole = async (id, roleData) => {
    try {
        const { data } = await axiosInstance.put(`/organization-roles/${id}`, roleData);
        return data;
    } catch (error) {
        console.error('Error updating organization role:', error);
        throw error;
    }
};

export const deleteOrganizationRole = async (id) => {
    try {
        const { data } = await axiosInstance.delete(`/organization-roles/${id}`);
        return data;
    } catch (error) {
        console.error('Error deleting organization role:', error);
        throw error;
    }
};

export const assignRoleToUser = async (roleId, userId, venueId = null) => {
    try {
        const { data } = await axiosInstance.post(`/organization-roles/${roleId}/assign/${userId}`, { venue_id: venueId });
        return data;
    } catch (error) {
        console.error('Error assigning role to user:', error);
        throw error;
    }
};

export const removeRoleFromUser = async (roleId, userId) => {
    try {
        const { data } = await axiosInstance.delete(`/organization-roles/${roleId}/remove/${userId}`);
        return data;
    } catch (error) {
        console.error('Error removing role from user:', error);
        throw error;
    }
};

export const fetchMyPermissions = async () => {
    try {
        const { data } = await axiosInstance.get('/organization-roles/my-permissions');
        return data.data;
    } catch (error) {
        console.error('Error fetching my permissions:', error);
        throw error;
    }
};

export const fetchUsersWithRoles = async (params = {}) => {
    try {
        const { data } = await axiosInstance.get('/organization-roles/users', { params });
        return data;
    } catch (error) {
        console.error('Error fetching users with roles:', error);
        throw error;
    }
};

// Integration API functions
export const fetchIntegrationHealth = async (integratorId) => {
    try {
        const { data } = await axiosInstance.get(`/organizations/integrators/${integratorId}/health`);
        return data;
    } catch (error) {
        console.error('Error fetching integration health:', error);
        throw error;
    }
};

export const fetchIntegrationLogs = async (integratorId, params = {}) => {
    try {
        const { data } = await axiosInstance.get(`/organizations/integrators/${integratorId}/logs`, { params });
        return data;
    } catch (error) {
        console.error('Error fetching integration logs:', error);
        throw error;
    }
};

export const rotateIntegrationCredentials = async (integratorId) => {
    try {
        const { data } = await axiosInstance.post(`/organizations/integrators/${integratorId}/rotate-credentials`);
        return data;
    } catch (error) {
        console.error('Error rotating integration credentials:', error);
        throw error;
    }
};

export const fetchIntegrationWebhooks = async (integratorId) => {
    try {
        const { data } = await axiosInstance.get(`/organizations/integrators/${integratorId}/webhooks`);
        return data;
    } catch (error) {
        console.error('Error fetching integration webhooks:', error);
        throw error;
    }
};

export const createIntegrationWebhook = async (integratorId, webhookData) => {
    try {
        const { data } = await axiosInstance.post(`/organizations/integrators/${integratorId}/webhooks`, webhookData);
        return data;
    } catch (error) {
        console.error('Error creating integration webhook:', error);
        throw error;
    }
};

export const deleteIntegrationWebhook = async (integratorId, webhookId) => {
    try {
        const { data } = await axiosInstance.delete(`/organizations/integrators/${integratorId}/webhooks/${webhookId}`);
        return data;
    } catch (error) {
        console.error('Error deleting integration webhook:', error);
        throw error;
    }
};

export default axiosInstance;
const isTokenExpired = (token) => {
    try {
        const decoded = JSON.parse(atob(token.split('.')[1]));
        const now = Math.floor(Date.now() / 1000);
        return decoded.exp < now;
    } catch (error) {
        return true;
    }
};
